(() => {
  function initPpbInquiry(section) {
    if (!section || section.dataset.ppbInquiryInitialized === 'true') return;
    section.dataset.ppbInquiryInitialized = 'true';

    const form = section.querySelector('form');
    const questionPanels = Array.from(section.querySelectorAll('[data-ppb-question]'))
      .sort((a, b) => Number(a.dataset.ppbStep) - Number(b.dataset.ppbStep));
    questionPanels.forEach((panel) => form.appendChild(panel));

    const panels = Array.from(section.querySelectorAll('[data-ppb-step]'));
    const summaryFields = Array.from(section.querySelectorAll('[data-ppb-summary-field]'));
    const fieldMap = new Map(summaryFields.map((field) => [field.dataset.ppbSummaryField, field]));
    const summaryBody = section.querySelector('[data-ppb-summary-body]');
    const startButton = section.querySelector('[data-ppb-start]');
    const progress = section.querySelector('[data-ppb-progress]');
    const progressBar = section.querySelector('[data-ppb-progress-bar]');
    let hasStarted = false;
    let lastExperience = '';

    // =========================
    // Shared Discovery Workflow
    // =========================

    function getInputs(fieldName) {
      return Array.from(section.querySelectorAll(`[data-ppb-field="${fieldName}"]`));
    }

    function getCombinedTimeValue(fieldName) {
      const group = section.querySelector(`[data-ppb-time-group="${fieldName}"]`);
      if (!group || group.querySelector('select:not(:disabled)') === null) return null;
      const hour = group.querySelector('[data-ppb-time-part="hour"]')?.value || '';
      const minute = group.querySelector('[data-ppb-time-part="minute"]')?.value || '';
      const period = group.querySelector('[data-ppb-time-part="period"]')?.value || '';
      return hour && minute && period ? `${hour}:${minute} ${period}` : '';
    }

    function getValue(fieldName) {
      const time = getCombinedTimeValue(fieldName);
      if (time !== null) return time;
      return getInputs(fieldName).reduce((values, input) => {
        if (input.disabled) return values;
        if ((input.type === 'checkbox' || input.type === 'radio') && !input.checked) return values;
        if (input.value) values.push(input.value);
        return values;
      }, []).join(', ');
    }

    function experience() {
      return getValue('Experience Type');
    }

    function activeWorkflow() {
      if (experience() === 'Rental Pickup & DIY Setup') return 'rental';
      if (experience() === 'Delivered & Styled Experience') return 'delivered';
      return '';
    }

    function dateNotFinalized() {
      return getValue('Event Date Type') === "We haven't finalized the date yet";
    }

    function todayValue() {
      const today = new Date();
      return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    }

    function syncEventType() {
      const customerType = getValue('Customer Type');
      section.querySelectorAll('[data-ppb-event-types]').forEach((group) => {
        const active = group.dataset.ppbEventTypes.split('|').includes(customerType);
        group.classList.toggle('is-visible', active);
        group.querySelectorAll('select').forEach((select) => {
          select.disabled = !active;
          if (!active) select.value = '';
        });
      });
    }

    function syncEventDates() {
      const dateType = getValue('Event Date Type');
      const message = section.querySelector('[data-ppb-date-message]');
      let clearedPastDate = false;
      section.querySelectorAll('[data-ppb-event-date]').forEach((input) => {
        input.min = todayValue();
        const group = input.closest('[data-ppb-date-fields]');
        const active = group.dataset.ppbDateFields === dateType && !dateNotFinalized();
        group.classList.toggle('is-visible', active);
        input.disabled = !active;
        if (!active) input.value = '';
        if (input.value && input.value < todayValue()) {
          input.value = '';
          clearedPastDate = true;
        }
      });
      if (message) message.textContent = clearedPastDate ? "Please select today's date or a future date." : '';

      section.querySelectorAll('[data-ppb-event-time] select').forEach((select) => {
        select.disabled = dateNotFinalized();
        select.required = !dateNotFinalized();
        if (dateNotFinalized()) select.value = '';
      });
    }

    function dateComplete() {
      if (dateNotFinalized()) return true;
      if (getValue('Event Date Type') === 'Single-day event') return Boolean(getValue('Event Date'));
      if (getValue('Event Date Type') === 'Multi-day event') return Boolean(getValue('Event Start Date') && getValue('Event End Date'));
      return false;
    }

    // =========================
    // Rental Pickup Workflow
    // =========================

    function rentalPipeSelected() {
      return getValue('Services Interested In').split(', ').includes('Pipe & Drape Backdrop Rental');
    }

    function syncRentalWorkflow() {
      const active = activeWorkflow() === 'rental';
      section.querySelectorAll('[data-ppb-workflow="rental"] input, [data-ppb-workflow="rental"] select, [data-ppb-workflow="rental"] textarea, [data-ppb-workflow-control="rental"]').forEach((control) => {
        control.disabled = !active;
        if ('required' in control) {
          const optionalVenue = control.dataset.ppbField === 'Venue Name';
          control.required = active && !optionalVenue && ['Event City', 'Event State'].includes(control.dataset.ppbField);
        }
      });
      const info = section.querySelector('[data-ppb-rental-info]');
      if (info) info.hidden = !(active && rentalPipeSelected());
      section.querySelectorAll('[data-ppb-schedule-date]').forEach((input) => {
        input.min = todayValue();
        input.required = active;
      });
      const pickupDate = getInputs('Pickup Date')[0];
      const returnDate = getInputs('Return Date')[0];
      if (returnDate) {
        const invalidOrder = active && pickupDate?.value && returnDate.value && returnDate.value < pickupDate.value;
        returnDate.setCustomValidity(invalidOrder ? 'Return date must be on or after pickup date.' : '');
        const message = section.querySelector('[data-ppb-validation-for="Return Date"]');
        if (message) {
          message.textContent = invalidOrder ? 'Return date must be on or after pickup date.' : 'Please complete this field.';
          if (!invalidOrder && returnDate.value) message.hidden = true;
        }
      }
      section.querySelectorAll('[data-ppb-workflow="rental"] [data-ppb-time-part]').forEach((select) => { select.required = active; });
    }

    function rentalDatesValid() {
      const pickup = getValue('Pickup Date');
      const returned = getValue('Return Date');
      return Boolean(pickup && returned && pickup >= todayValue() && returned >= todayValue() && returned >= pickup);
    }

    // =========================
    // Delivered & Styled Workflow
    // =========================

    function deliveredPipeSelected() {
      return activeWorkflow() === 'delivered' && getValue('Services Interested In').split(', ').includes('Pipe & Drape');
    }

    function syncDeliveredWorkflow() {
      const active = activeWorkflow() === 'delivered';
      section.querySelectorAll('[data-ppb-workflow="delivered"] input, [data-ppb-workflow="delivered"] select, [data-ppb-workflow="delivered"] textarea').forEach((control) => {
        control.disabled = !active;
      });
      section.querySelectorAll('[data-ppb-workflow="delivered"] [data-ppb-time-part]').forEach((select) => {
        const timelineActive = active && !dateNotFinalized();
        select.disabled = !timelineActive;
        select.required = timelineActive;
        if (!timelineActive) select.value = '';
      });

      const status = getValue('Venue Status');
      section.querySelectorAll('[data-ppb-venue-fields], [data-ppb-venue-note]').forEach((group) => {
        const target = group.dataset.ppbVenueFields || group.dataset.ppbVenueNote;
        const show = active && target === status;
        group.classList.toggle('is-visible', show);
        group.querySelectorAll('input').forEach((input) => {
          input.disabled = !show;
          input.required = show;
          if (!show) input.value = '';
        });
      });

      const role = getValue('Pipe & Drape Role');
      const other = section.querySelector('[data-ppb-pipe-drape-other]');
      if (other) {
        const show = deliveredPipeSelected() && role === 'Other';
        other.classList.toggle('is-visible', show);
        other.querySelectorAll('textarea').forEach((input) => {
          input.disabled = !show;
          input.required = show;
          if (!show) input.value = '';
        });
      }
    }

    // =========================
    // Shared Personalization Workflow
    // =========================

    function meaningful(fieldName) {
      return Boolean(getValue(fieldName).trim());
    }

    function servicesInclude(value) {
      return getValue('Services Interested In').split(', ').includes(value);
    }

    function syncConditionalDescriptions() {
      section.querySelectorAll('[data-ppb-something-else-panel]').forEach((panel) => {
        const active = panel.dataset.ppbWorkflow === activeWorkflow() && servicesInclude('Something Else');
        panel.querySelectorAll('textarea').forEach((input) => {
          input.disabled = !active;
          input.required = active;
          if (!active) input.value = '';
        });
      });
    }

    function syncInspiration() {
      const active = getValue('Inspiration Preference') === 'Yes';
      const fields = section.querySelector('[data-ppb-inspiration-fields]');
      if (!fields) return;
      fields.classList.toggle('is-visible', active);
      fields.querySelectorAll('input').forEach((input) => {
        input.disabled = !active;
        input.required = active;
        if (!active) input.value = '';
      });
    }

    function syncReferral() {
      const active = getValue('Referral Source') === 'Other';
      const fields = section.querySelector('[data-ppb-referral-other]');
      if (!fields) return;
      fields.classList.toggle('is-visible', active);
      fields.querySelectorAll('input').forEach((input) => {
        input.disabled = !active;
        input.required = active;
        if (!active) input.value = '';
      });
    }

    function formatPhone(value) {
      const digits = value.replace(/\D/g, '').slice(0, 10);
      if (digits.length <= 3) return digits;
      if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }

    function syncPhone() {
      const phone = getInputs('Phone')[0];
      if (phone) phone.value = formatPhone(phone.value);
    }

    function normalizedNamePart(fieldName) {
      return getValue(fieldName).trim().replace(/\s+/g, ' ');
    }

    function combinedName() {
      return [normalizedNamePart('First Name'), normalizedNamePart('Last Name')].filter(Boolean).join(' ');
    }

    function syncStructuredName() {
      const firstName = normalizedNamePart('First Name');
      const lastName = normalizedNamePart('Last Name');
      const combined = section.querySelector('[data-ppb-combined-name]');
      const structuredFirst = section.querySelector('[data-ppb-structured-name="First Name"]');
      const structuredLast = section.querySelector('[data-ppb-structured-name="Last Name"]');
      if (combined) combined.value = [firstName, lastName].filter(Boolean).join(' ');
      if (structuredFirst) structuredFirst.value = firstName;
      if (structuredLast) structuredLast.value = lastName;
    }

    // =========================
    // Validation and Submission
    // =========================

    function isPrimarilyEnglish(value) {
      const text = value.trim().toLowerCase();
      if (!text) return true;
      const words = text.match(/[a-zÀ-ÿ']+/g) || [];
      if (words.length <= 3) return true;
      const englishWords = new Set(['a','about','after','all','also','an','and','area','as','at','backdrop','backdrops','balloon','birthday','black','brand','branded','celebration','ceremony','columns','corporate','custom','decor','display','event','for','from','gold','have','help','in','is','it','like','looking','love','modern','need','of','on','or','our','party','photo','planning','please','room','setup','share','stage','style','the','their','this','to','use','we','welcome','with','would','you']);
      const nonEnglishWords = new Set(['con','de','del','des','el','en','et','la','las','le','les','los','para','por','que','un','una','une','y']);
      const englishCount = words.filter((word) => englishWords.has(word.replace(/^'+|'+$/g, ''))).length;
      const nonEnglishCount = words.filter((word) => nonEnglishWords.has(word.replace(/^'+|'+$/g, ''))).length;
      const letters = text.match(/\p{L}/gu) || [];
      const ascii = text.match(/[a-z]/g) || [];
      if (letters.length && ascii.length / letters.length < 0.7) return false;
      if (nonEnglishCount >= 2 && nonEnglishCount > englishCount) return false;
      return !(words.length >= 6 && englishCount === 0);
    }

    function englishValid(fieldName) {
      return isPrimarilyEnglish(getValue(fieldName));
    }

    function shouldIncludePanel(panel) {
      const workflow = panel.dataset.ppbWorkflow;
      if (workflow && workflow !== activeWorkflow()) return false;
      if (panel.hasAttribute('data-ppb-something-else-panel')) return servicesInclude('Something Else');
      if (panel.hasAttribute('data-ppb-pipe-drape')) return deliveredPipeSelected();
      if (panel.hasAttribute('data-ppb-event-time')) return !dateNotFinalized();
      if (['7.3', '7.4'].includes(panel.dataset.ppbStep)) return !dateNotFinalized();
      return true;
    }

    function panelComplete(panel) {
      const step = panel.dataset.ppbStep;
      const rules = {
        '1': () => meaningful('Customer Type'),
        '2': () => meaningful('Event Type'),
        '3': dateComplete,
        '3.5': () => meaningful('Event Start Time'),
        '3.55': () => meaningful('Event End Time'),
        '3.58': () => meaningful('Experience Type'),
        '4': () => meaningful('Services Interested In'),
        '4.1': () => meaningful('Something Else Requested') && englishValid('Something Else Requested'),
        '4.2': () => meaningful('Pickup Date') && getValue('Pickup Date') >= todayValue(),
        '4.3': () => meaningful('Pickup Time'),
        '4.4': rentalDatesValid,
        '4.5': () => meaningful('Return Time'),
        '4.6': () => meaningful('Budget Range'),
        '4.7': () => meaningful('Event City') && meaningful('Event State'),
        '5': () => meaningful('Services Interested In'),
        '5.1': () => meaningful('Something Else Requested') && englishValid('Something Else Requested'),
        '5.2': () => meaningful('Pipe & Drape Role') && (getValue('Pipe & Drape Role') !== 'Other' || (meaningful('Pipe & Drape Other Description') && englishValid('Pipe & Drape Other Description'))),
        '6': () => meaningful('Venue Status'),
        '7': () => getValue('Venue Status') === 'Yes' ? meaningful('Venue Name') && meaningful('Venue City/State') : meaningful('General Location'),
        '7.2': () => meaningful('Venue Type'),
        '7.3': () => meaningful('Setup Begin Time'),
        '7.4': () => meaningful('Breakdown Begin Time'),
        '7.5': () => meaningful('Budget Range'),
        '9': () => meaningful('Inspiration Preference') && (getValue('Inspiration Preference') !== 'Yes' || inspirationValid()),
        '9.1': () => meaningful('Vision Notes') && englishValid('Vision Notes'),
        '14': () => meaningful('First Name') && meaningful('Last Name') && meaningful('Email') && referralValid() && sharedEnglishValid()
      };
      return rules[step] ? Boolean(rules[step]()) : true;
    }

    function inspirationValid() {
      const input = getInputs('Inspiration Link')[0];
      return Boolean(input && input.value.trim() && input.checkValidity());
    }

    function referralValid() {
      if (!meaningful('Referral Source')) return false;
      return getValue('Referral Source') !== 'Other' || (meaningful('Referral Source Other') && englishValid('Referral Source Other'));
    }

    function sharedEnglishValid() {
      return ['Vision Notes', 'Something Else Requested', 'Pipe & Drape Other Description', 'Referral Source Other'].every(englishValid);
    }

    function resetWorkflow(workflow) {
      if (!workflow) return;
      section.querySelectorAll(`[data-ppb-workflow="${workflow}"] input, [data-ppb-workflow="${workflow}"] select, [data-ppb-workflow="${workflow}"] textarea, [data-ppb-workflow-control="${workflow}"]`).forEach((control) => {
        if (control.type === 'checkbox' || control.type === 'radio') control.checked = false;
        else control.value = '';
      });
    }

    function handleExperienceChange() {
      const current = experience();
      if (lastExperience && current !== lastExperience) {
        const oldWorkflow = lastExperience === 'Rental Pickup & DIY Setup' ? 'rental' : 'delivered';
        resetWorkflow(oldWorkflow);
      }
      lastExperience = current;
    }

    function handleExclusiveService(input) {
      if (!input.matches('[data-ppb-field="Services Interested In"]') || !input.checked) return;
      const group = Array.from(section.querySelectorAll(`input[name="${input.name}"]`));
      const notSure = group.find((item) => item.hasAttribute('data-ppb-not-sure-service'));
      if (!notSure) return;
      if (input === notSure) group.forEach((item) => { if (item !== input) item.checked = false; });
      else notSure.checked = false;
    }

    function firstControl(panel) {
      const step = panel.dataset.ppbStep;
      const fieldByStep = {
        '1':'Customer Type','2':'Event Type','3':'Event Date Type','3.5':'Event Start Time','3.55':'Event End Time','3.58':'Experience Type','4':'Services Interested In','4.1':'Something Else Requested','4.2':'Pickup Date','4.3':'Pickup Time','4.4':'Return Date','4.5':'Return Time','4.6':'Budget Range','4.7':'Event City','5':'Services Interested In','5.1':'Something Else Requested','5.2':'Pipe & Drape Role','6':'Venue Status','7':'Venue Name','7.2':'Venue Type','7.3':'Setup Begin Time','7.4':'Breakdown Begin Time','7.5':'Budget Range','9':'Inspiration Preference','9.1':'Vision Notes','14':'First Name'
      };
      const fieldName = fieldByStep[step];
      if (step === '4.7') return getInputs('Event City').find((input) => !input.disabled && !input.value.trim()) || getInputs('Event State').find((input) => !input.disabled && !input.value.trim());
      if (step === '5.2' && getValue('Pipe & Drape Role') === 'Other') return getInputs('Pipe & Drape Other Description').find((input) => !input.disabled);
      if (step === '7') {
        const venueFields = getValue('Venue Status') === 'Yes' ? ['Venue Name', 'Venue City/State'] : ['General Location'];
        return venueFields.flatMap(getInputs).find((input) => !input.disabled && !input.value.trim());
      }
      if (step === '9' && getValue('Inspiration Preference') === 'Yes') return getInputs('Inspiration Link').find((input) => !input.disabled);
      if (step === '14') {
        const names = ['First Name', 'Last Name', 'Email', 'Referral Source'];
        if (getValue('Referral Source') === 'Other') names.push('Referral Source Other');
        return names.flatMap(getInputs).find((input) => !input.disabled && (!input.value.trim() || !input.checkValidity()));
      }
      const timeGroup = fieldName && section.querySelector(`[data-ppb-time-group="${fieldName}"]`);
      if (timeGroup) return Array.from(timeGroup.querySelectorAll('select:not(:disabled)')).find((select) => !select.value) || timeGroup.querySelector('select:not(:disabled)');
      return (fieldName && getInputs(fieldName).find((input) => !input.disabled && (!input.value.trim() || (input.matches(':checked') === false && ['radio', 'checkbox'].includes(input.type))))) || panel.querySelector('input:not(:disabled), select:not(:disabled), textarea:not(:disabled)');
    }

    function showInvalid(field) {
      if (!field) return;
      const fieldName = field.dataset.ppbField || field.closest('[data-ppb-time-group]')?.dataset.ppbTimeGroup;
      const message = fieldName && section.querySelector(`[data-ppb-validation-for="${fieldName}"]`);
      if (message) message.hidden = false;
      field.closest('[data-ppb-question]')?.classList.add('is-visible');
      field.scrollIntoView({ behavior: 'smooth', block: 'center' });
      field.focus({ preventScroll: true });
    }

    function revealPanels() {
      let canReveal = hasStarted;
      questionPanels.forEach((panel) => {
        const show = canReveal && shouldIncludePanel(panel);
        panel.classList.toggle('is-visible', show);
        panel.querySelectorAll('input, select, textarea').forEach((control) => {
          if (!show && !control.matches('[data-ppb-field="Experience Type"]')) control.setAttribute('aria-hidden', 'true');
          else control.removeAttribute('aria-hidden');
        });
        if (show && !panelComplete(panel)) canReveal = false;
      });
    }

    function syncProgress() {
      if (!progress || !progressBar) return;
      progress.classList.toggle('is-visible', hasStarted);
      const included = questionPanels.filter(shouldIncludePanel);
      const complete = included.filter(panelComplete).length;
      progressBar.style.width = `${hasStarted && included.length ? Math.max(4, Math.round(complete / included.length * 100)) : 0}%`;
    }

    const commonFields = ['Customer Type','Event Type','Event Date Type','Event Date','Event Start Date','Event End Date','Event Start Time','Event End Time','Date Not Finalized status','Experience Type','Inspiration Preference','Inspiration Link','Vision Notes','Preferred Contact Method','Referral Source','Referral Source Other'];
    const sharedSummaryFields = ['Customer Type','Event Type','Event Date Type','Event Date','Event Start Date','Event End Date','Event Start Time','Event End Time','Experience Type'];
    const rentalSummary = ['Services Interested In','Something Else Requested','Pickup Date','Pickup Time','Return Date','Return Time','Budget Range','Event City','Event State','Venue Name'];
    const deliveredSummary = ['Services Interested In','Something Else Requested','Pipe & Drape Role','Pipe & Drape Other Description','Venue Status','Venue Name','Venue City/State','General Location','Venue Type','Setup Begin Time','Breakdown Begin Time','Budget Range'];

    function syncHiddenSummary(forSubmission = false) {
      const activeFields = new Set([...commonFields, ...(activeWorkflow() === 'rental' ? rentalSummary : activeWorkflow() === 'delivered' ? deliveredSummary : [])]);
      summaryFields.forEach((hidden) => {
        const name = hidden.dataset.ppbSummaryField;
        hidden.disabled = forSubmission && !activeFields.has(name);
        hidden.value = activeFields.has(name) ? getValue(name) : '';
      });
      const dateStatus = fieldMap.get('Date Not Finalized status');
      if (dateStatus) dateStatus.value = dateNotFinalized() ? 'Yes - date not finalized' : 'No';

      const labels = activeWorkflow() === 'rental' ? {
        'Experience Type':'Experience','Services Interested In':'Selected Rental Services','Something Else Requested':'Something Else Description','Budget Range':'Pickup Budget','Venue Name':'Venue Name'
      } : {
        'Experience Type':'Experience','Services Interested In':'Selected Services','Something Else Requested':'Something Else Description','Pipe & Drape Role':'Pipe & Drape Solution','Budget Range':'Delivered & Styled Budget'
      };
      const fields = [...sharedSummaryFields, ...(activeWorkflow() === 'rental' ? rentalSummary : deliveredSummary), 'Inspiration Link', 'Vision Notes', 'Preferred Contact Method', 'Referral Source', 'Referral Source Other'];
      const lines = fields.map((name) => [labels[name] || name, fieldMap.get(name)?.value || getValue(name)]).filter(([, value]) => value);
      lines.push(['Name', combinedName()], ['Email', getValue('Email')], ['Phone', getValue('Phone')]);
      if (summaryBody) summaryBody.value = lines.filter(([, value]) => value).map(([label, value]) => `${label}: ${value}`).join('\n');
    }

    function sync() {
      handleExperienceChange();
      syncEventType();
      syncEventDates();
      syncRentalWorkflow();
      syncDeliveredWorkflow();
      syncConditionalDescriptions();
      syncInspiration();
      syncReferral();
      syncStructuredName();
      syncHiddenSummary(false);
      revealPanels();
      syncProgress();
    }

    startButton?.addEventListener('click', () => {
      hasStarted = true;
      sync();
      const first = panels.find((panel) => panel.dataset.ppbStep === '1');
      first?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      first?.querySelector('input, select, textarea')?.focus({ preventScroll: true });
    });

    section.addEventListener('change', (event) => {
      handleExclusiveService(event.target);
      sync();
    });
    section.addEventListener('input', (event) => {
      if (event.target.matches('[data-ppb-field="Phone"]')) syncPhone();
      const message = event.target.dataset.ppbField && section.querySelector(`[data-ppb-validation-for="${event.target.dataset.ppbField}"]`);
      if (message && event.target.value.trim()) message.hidden = true;
      sync();
    });

    form?.addEventListener('invalid', (event) => {
      if (!event.target.matches('[data-ppb-field], [data-ppb-time-part]')) return;
      event.preventDefault();
      showInvalid(event.target);
    }, true);

    form?.addEventListener('submit', (event) => {
      sync();
      const incomplete = questionPanels.filter(shouldIncludePanel).find((panel) => !panelComplete(panel));
      if (incomplete) {
        event.preventDefault();
        event.stopPropagation();
        showInvalid(firstControl(incomplete));
        return;
      }
      if (activeWorkflow() === 'rental') resetWorkflow('delivered');
      if (activeWorkflow() === 'delivered') resetWorkflow('rental');
      syncStructuredName();
      syncHiddenSummary(true);
    });

    syncPhone();
    sync();
  }

  function initAllPpbInquiries() {
    document.querySelectorAll('.ppb-inquiry').forEach(initPpbInquiry);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAllPpbInquiries);
  else initAllPpbInquiries();

  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll('.ppb-inquiry').forEach(initPpbInquiry);
  });
})();